import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function VideoManager() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    youtube_url: '',
    description: '',
    order: 0,
    active: true
  });

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${API}/videos?active_only=false`);
      setVideos(response.data);
    } catch (err) {
      console.error('Error fetching videos:', err);
    } finally {
      setLoading(false);
    }
  };

  const extractYouTubeId = (url) => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\?\/\s]+)/,
      /youtube\.com\/watch\?.*v=([^&\?\/\s]+)/
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('admin_token');

    try {
      if (editingVideo) {
        await axios.put(`${API}/videos/${editingVideo.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/videos`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      resetForm();
      fetchVideos();
    } catch (err) {
      console.error('Error saving video:', err);
      alert('Failed to save video');
    }
  };

  const handleEdit = (video) => {
    setEditingVideo(video);
    setFormData({
      title: video.title,
      youtube_url: video.youtube_url,
      description: video.description || '',
      order: video.order,
      active: video.active
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this video?')) return;

    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(`${API}/videos/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchVideos();
    } catch (err) {
      console.error('Error deleting video:', err);
      alert('Failed to delete video');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      youtube_url: '',
      description: '',
      order: 0,
      active: true
    });
    setEditingVideo(null);
    setShowForm(false);
  };

  if (loading) {
    return <div className="text-center py-12 text-gray-400">Loading videos...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-2xl font-light">MANAGE YOUTUBE VIDEOS</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          className="px-6 py-2 bg-white text-black font-medium hover:bg-gray-200 transition-colors"
        >
          {showForm ? 'CANCEL' : 'ADD VIDEO'}
        </button>
      </div>

      {showForm && (
        <div className="bg-zinc-900 border border-zinc-800 p-6 mb-8">
          <h3 className="text-xl font-light mb-6">{editingVideo ? 'EDIT VIDEO' : 'ADD NEW VIDEO'}</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Video Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                required
                className="w-full bg-black border border-zinc-700 px-4 py-2 text-white focus:outline-none focus:border-white"
                placeholder="Amazing Forex Trading Tips"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">YouTube URL *</label>
              <input
                type="url"
                value={formData.youtube_url}
                onChange={(e) => setFormData({...formData, youtube_url: e.target.value})}
                required
                className="w-full bg-black border border-zinc-700 px-4 py-2 text-white focus:outline-none focus:border-white"
                placeholder="https://youtu.be/iw7BGkcDToc"
              />
              <p className="text-xs text-gray-500 mt-1">Paste full YouTube URL (youtu.be or youtube.com)</p>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows="2"
                className="w-full bg-black border border-zinc-700 px-4 py-2 text-white focus:outline-none focus:border-white"
                placeholder="Brief description of the video"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Display Order</label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({...formData, order: parseInt(e.target.value)})}
                  className="w-full bg-black border border-zinc-700 px-4 py-2 text-white focus:outline-none focus:border-white"
                  min="0"
                />
                <p className="text-xs text-gray-500 mt-1">Lower numbers appear first (0, 1, 2...)</p>
              </div>

              <div className="flex items-end">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="active"
                    checked={formData.active}
                    onChange={(e) => setFormData({...formData, active: e.target.checked})}
                    className="w-4 h-4"
                  />
                  <label htmlFor="active" className="text-sm text-gray-400">Active</label>
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <button
                type="submit"
                className="px-6 py-2 bg-white text-black font-medium hover:bg-gray-200 transition-colors"
              >
                {editingVideo ? 'UPDATE' : 'ADD'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-6 py-2 border border-zinc-700 text-white hover:bg-zinc-800 transition-colors"
              >
                CANCEL
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-6">
        {videos.length === 0 ? (
          <div className="col-span-3 text-center py-12 text-gray-500">No videos yet. Add your first video!</div>
        ) : (
          videos.map((video) => {
            const videoId = extractYouTubeId(video.youtube_url);
            return (
              <div key={video.id} className="bg-zinc-900 border border-zinc-800 overflow-hidden">
                {/* Video Preview */}
                <div className="relative pb-[56.25%] bg-black">
                  <iframe
                    className="absolute inset-0 w-full h-full"
                    src={`https://www.youtube.com/embed/${videoId}`}
                    title={video.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>

                {/* Video Info */}
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-medium flex-1">{video.title}</h3>
                    {!video.active && (
                      <span className="text-xs px-2 py-1 bg-red-900/30 text-red-400 border border-red-900 ml-2">INACTIVE</span>
                    )}
                  </div>
                  
                  {video.description && (
                    <p className="text-sm text-gray-400 mb-3">{video.description}</p>
                  )}

                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-4">
                    <span>Order: {video.order}</span>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(video)}
                      className="flex-1 px-4 py-2 border border-zinc-700 text-sm hover:bg-zinc-800 transition-colors"
                    >
                      EDIT
                    </button>
                    <button
                      onClick={() => handleDelete(video.id)}
                      className="flex-1 px-4 py-2 border border-red-900 text-red-400 text-sm hover:bg-red-900/20 transition-colors"
                    >
                      DELETE
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default VideoManager;
